import { useState } from "react";
import { AlertTriangle, Eye, Send } from "lucide-react";

import CuoConfirmDialog from "@/app/admin/_common/CuoConfirmDialog";
import { Switch } from "@/app/admin/_common/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/admin/_common/ui/select";
import { cn } from "@/app/_utils/cn";
import type { Automation } from "@/app/_services/AutomationService";

import { STATUS_LABEL } from "../../_cmp/automationStatus";

/**
 * The cutover control.
 *
 * Turning an automation live is the single action in this UI that can cause a real
 * email to reach a customer, so it is confirmed rather than a bare toggle. Turning it
 * back to shadow is not confirmed — a rollback should be as fast as possible, and the
 * failure mode of an accidental rollback (the legacy send resumes) is harmless.
 */
export default function KillSwitchBar({
  automation,
  isSaving,
  onChange,
}: {
  automation: Automation;
  isSaving: boolean;
  onChange: (body: { status?: string; mode?: string }) => void;
}) {
  const [confirmLive, setConfirmLive] = useState(false);
  const isLive = automation.mode === "live";
  const isActive = automation.status === "active";

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-4 rounded-md border px-4 py-3",
        isLive && isActive
          ? "border-rose-200 bg-rose-50"
          : "border-border bg-card"
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {isLive && isActive ? (
          <Send className="size-4 flex-none text-rose-600" />
        ) : (
          <Eye className="size-4 flex-none text-muted-foreground" />
        )}
        <div className="flex min-w-0 flex-col">
          <span className="text-[13px] font-medium text-foreground">
            {isLive && isActive
              ? "Sending for real"
              : isLive
                ? "Live, but not active"
                : "Shadow — nothing is sent"}
          </span>
          <span className="truncate text-[12px] text-muted-foreground">
            {isLive && isActive
              ? "This automation is delivering messages to real recipients."
              : isLive
                ? "Set status to Active for this to run."
                : "Runs are recorded and templates rendered, but no message leaves the system."}
          </span>
        </div>
      </div>

      <div className="flex flex-none items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-muted-foreground">Status</span>
          <Select
            value={automation.status}
            onValueChange={(status) => onChange({ status })}
            disabled={isSaving}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_LABEL).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[12.5px] text-muted-foreground">Live</span>
          <Switch
            checked={isLive}
            disabled={isSaving}
            aria-label="Send for real"
            onCheckedChange={(checked) => {
              if (checked) {
                setConfirmLive(true);
              } else {
                onChange({ mode: "shadow" });
              }
            }}
          />
        </div>
      </div>

      <CuoConfirmDialog
        open={confirmLive}
        onClose={() => setConfirmLive(false)}
        title="Send for real?"
        message={
          `"${automation.name}" will start delivering messages to real recipients, and ` +
          "the existing hardcoded send for this concern will stop. You can switch back " +
          "to shadow at any time — it takes effect on the next event."
        }
        confirmText="Go live"
        icon={<AlertTriangle />}
        onConfirm={async () => {
          onChange({ mode: "live" });
          setConfirmLive(false);
        }}
      />
    </div>
  );
}
